<script setup lang="ts">
/**
 * Interactive tutorials — the running overlay: four scrim rectangles around a
 * spotlit [data-tour] target, and the step card anchored beside it.
 *
 * Ported from design_handoff_interactive_tutorial/blume-live-tour.jsx
 * (`TourOverlay`). The maths lives in engine.ts; what is here is the reactive
 * state, the detection listeners and the markup.
 *
 * The component is deliberately ignorant of tours, phases and sessionStorage —
 * it renders the checkpoint it is handed and emits what happened. TourRoot.vue
 * owns which checkpoint that is and what advancing means.
 */

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { TourCheckpoint } from './registry'
import {
  autoScrollDelta,
  cardPlacement,
  CLICK_ADVANCE_DELAY_MS,
  headerBottom,
  MIN_TOUR_VIEWPORT,
  MISSING_TARGET_TIMEOUT_MS,
  navigatesAwayFrom,
  renderMarkdownLite,
  scrimRects,
  spotlightHole,
  tourEl,
  type Box,
  type TourExitReason,
} from './engine'

const props = defineProps<{
  checkpoints: TourCheckpoint[]
  steps: string[]
  index: number
}>()

const emit = defineEmits<{
  (e: 'advance'): void
  /** An intro's "Skip to step N"; the parent resolves where that lands. */
  (e: 'skip-step'): void
  /** The reason decides where the parent puts the "tutorial ended" toast. */
  (e: 'exit', reason: TourExitReason): void
  /** One checkpoint back. Never offered across a checkpoint that ended a page. */
  (e: 'back'): void
  /**
   * The florist acted ahead: they clicked the target of a LATER checkpoint in
   * the same step (created the event from the "new event form" intro without
   * pressing Show me). The tour catches up to the checkpoint after it.
   */
  (e: 'jump', index: number): void
}>()

const cardRef = ref<HTMLElement | null>(null)

const rect = ref<Box | null>(null)
const ready = ref(false)
const viewportWidth = ref(window.innerWidth)
const viewportHeight = ref(window.innerHeight)
const navBottom = ref(0)
const cardHeight = ref(190)

const cp = computed<TourCheckpoint | undefined>(() => props.checkpoints[props.index])
const intro = computed(() => cp.value?.mode === 'intro')

/**
 * A viewport too narrow to anchor a 300px card beside anything docks the card
 * bottom-right rather than ending the run. The spotlight and scrim stay: the
 * florist still has to click the real control, and losing the highlight would
 * leave them with a card pointing at nothing.
 */
const narrow = computed(() => viewportWidth.value < MIN_TOUR_VIEWPORT)

const hole = computed(() => (intro.value ? null : spotlightHole(rect.value)))

const scrims = computed(() =>
  intro.value
    ? []
    : scrimRects(hole.value, viewportWidth.value, viewportHeight.value, navBottom.value)
)

const placement = computed(() => {
  if (intro.value || narrow.value) return null
  return cardPlacement(
    hole.value,
    viewportWidth.value,
    viewportHeight.value,
    navBottom.value,
    cardHeight.value
  )
})

/** Docked = the card has no target to sit beside (missing, degenerate, or too narrow a viewport). */
const docked = computed(() => !intro.value && placement.value === null)

/**
 * The target is on another tab of the editor, and the spotlight is on that
 * tab's button instead (see TourCheckpoint.screen). Set by measure().
 */
const onOtherTab = ref(false)

const SCREEN_LABELS: Record<string, string> = {
  details: 'Details',
  arrangements: 'Arrangements',
  review: 'Review & send',
}
const otherTabLabel = computed(() => SCREEN_LABELS[cp.value?.screen ?? ''] ?? '')

/**
 * Back is offered when there is a previous checkpoint on THIS page. A
 * checkpoint that ended the page (ne-save, advanceImmediately) cannot be
 * returned to: its target is on the screen that was left.
 */
const canGoBack = computed(() => {
  if (props.index <= 0) return false
  const previous = props.checkpoints[props.index - 1]
  return !!previous && !previous.advanceImmediately
})

const cardStyle = computed(() => {
  const at = placement.value
  if (!at) return undefined
  return { top: `${at.top}px`, left: `${at.left}px`, width: `${at.width}px` }
})

const stepLabel = computed(() => {
  const step = cp.value?.step ?? 0
  return `Step ${step + 1} of ${props.steps.length} · ${props.steps[step] ?? ''}`
})

const bodyHtml = computed(() => renderMarkdownLite(cp.value?.body ?? ''))

const skipLabel = computed(() => {
  const step = cp.value?.step ?? 0
  return step < props.steps.length - 1 ? `Skip to step ${step + 2}` : 'Skip — I know this one'
})

// ── measurement ───────────────────────────────────────────────────────────
// One measure pass: viewport, header, target rect, card height. Called from a
// requestAnimationFrame loop (as the prototype does — the target can move for
// reasons no event reports, e.g. a panel expanding above it), and directly on
// mount, checkpoint change, resize and scroll so the first paint is never a
// frame late and so tests never have to wait for an animation frame.

let scrolledForCheckpoint = false
let focusedForCheckpoint = false
let seededForCheckpoint = false
let revealedForCheckpoint = false

function measure(): void {
  viewportWidth.value = window.innerWidth
  viewportHeight.value = window.innerHeight
  navBottom.value = headerBottom()

  const real = tourEl(cp.value?.target)

  // Inside a folded card: open it, once, through its own toggle. Done before
  // the tab check so a folded card on the right tab is never read as "wrong
  // tab" — the tab's button exists on every tab.
  if (!real && cp.value?.reveal && !revealedForCheckpoint) {
    const toggle = tourEl(cp.value.reveal)
    if (toggle) {
      revealedForCheckpoint = true
      toggle.click()
    }
  }

  // Not on this tab: point at the tab that has it. Only a target that is
  // absent from the DOM altogether counts — one that is present but 0×0 is
  // mid-transition and the docked wait below already handles it. A tab that is
  // ALREADY active is not the problem (its button exists on every tab), so it
  // is never offered as the answer; the missing-target wait handles that.
  const candidate = !real && cp.value?.screen ? tourEl(`qstep-${cp.value.screen}`) : null
  const tab = candidate && candidate.dataset.state !== 'active' ? candidate : null
  const nowOnOtherTab = !!tab
  if (nowOnOtherTab !== onOtherTab.value) {
    onOtherTab.value = nowOnOtherTab
    // The spotlight is moving between the tab button and the real target:
    // let the auto-scroll run again for wherever it lands.
    scrolledForCheckpoint = false
  }
  const el = real ?? tab

  // A field that already holds a value is ready as it stands (Hannah,
  // 2026-09-08): a studio's saved pricing defaults arrive prefilled, and the
  // florist should see Next lit and be free to keep the default or change it.
  // Seeded ONCE, the first time the target exists for this checkpoint — a
  // field inside a modal the previous checkpoint opened is not there on entry
  // — and from then on the live input/change events own readiness, so clearing
  // the field still turns Next off. A <select> on its prompt has an empty value
  // and seeds as not ready, which is what "no client picked yet" should be.
  if (real && !seededForCheckpoint) {
    seededForCheckpoint = true
    if (cp.value?.mode === 'input') {
      ready.value = ((real as HTMLInputElement).value || '').trim().length > 0
    }
  }

  const r = el ? el.getBoundingClientRect() : null
  const next: Box | null = r
    ? {
        top: Math.round(r.top),
        left: Math.round(r.left),
        width: Math.round(r.width),
        height: Math.round(r.height),
      }
    : null

  const prev = rect.value
  const changed =
    !!prev !== !!next ||
    (!!prev &&
      !!next &&
      (prev.top !== next.top ||
        prev.left !== next.left ||
        prev.width !== next.width ||
        prev.height !== next.height))
  if (changed) rect.value = next

  if (cardRef.value) {
    const h = cardRef.value.offsetHeight
    if (h > 0 && h !== cardHeight.value) cardHeight.value = h
  }

  // Everything below needs a target with a real size. A 0x0 rect is the same
  // "no target yet" that spotlightHole() rejects — an element that is
  // display:none, mid-transition or detached — so nothing may be spent on it.
  // Auto-scroll in particular is ONE SHOT per checkpoint: letting a degenerate
  // rect consume it scrolls to the top-left corner and then declines to scroll
  // again when the element lands below the fold, which is exactly the
  // late-appearing field (inside a modal the previous checkpoint opened) this
  // path exists for.
  if (!spotlightHole(next)) return
  clearMissingTargetTimer()

  if (next && !scrolledForCheckpoint) {
    scrolledForCheckpoint = true
    const delta = autoScrollDelta(next, viewportHeight.value)
    if (delta !== null) window.scrollBy({ top: delta, behavior: 'smooth' })
  }

  // Focus ONCE, the first time the target actually exists for this checkpoint —
  // not on every frame the rect changes, which is what the prototype's effect
  // did and which would snatch focus back from a florist who tabbed away. The
  // target often appears late (the field is inside a modal the previous
  // checkpoint opened), so this cannot be done on entry alone.
  if (real && !focusedForCheckpoint) {
    focusedForCheckpoint = true
    focusTarget()
  }
}

let frame = 0

function loop(): void {
  measure()
  frame = window.requestAnimationFrame(loop)
}

// ── missing target ────────────────────────────────────────────────────────
// A checkpoint whose element never appears (a layout change, a feature the
// studio doesn't use, or simply the wrong page for one frame after a reload)
// waits, docked, and is then skipped silently. Never a dead card
// (handoff README, "Interactions & edge behaviour").

let missingTimer = 0

function clearMissingTargetTimer(): void {
  if (missingTimer) {
    window.clearTimeout(missingTimer)
    missingTimer = 0
  }
}

function startMissingTargetTimer(): void {
  clearMissingTargetTimer()
  if (intro.value || !cp.value?.target) return

  missingTimer = window.setTimeout(() => {
    missingTimer = 0
    if (spotlightHole(rect.value)) return
    console.warn(
      `[tour] no [data-tour="${cp.value?.target}"] after ${MISSING_TARGET_TIMEOUT_MS}ms — skipping "${cp.value?.title}"`
    )
    emit('advance')
  }, MISSING_TARGET_TIMEOUT_MS)
}

// ── detection ─────────────────────────────────────────────────────────────
// Capture-phase listeners on document, so the tour sees the interaction
// wherever it happens and whatever the app does with it — but they only ever
// observe. Advancing on click is deferred so the app's own handler runs first.

let clickTimer = 0

function within(event: Event): boolean {
  const node = event.target as Node | null
  if (!node) return false
  const el = tourEl(cp.value?.target)
  const alt = cp.value?.alt ? tourEl(cp.value.alt) : null
  return !!(el?.contains(node) || alt?.contains(node))
}

/**
 * Index of a later click checkpoint in the CURRENT step whose target (or alt)
 * this click landed in, or null. Same step only: acting ahead within a step is
 * the florist getting on with it; a click on another step's control is left to
 * that step's own checkpoints.
 */
function laterClickCheckpointHit(event: Event): number | null {
  const node = event.target as Node | null
  const current = cp.value
  if (!node || !current) return null
  for (let k = props.index + 1; k < props.checkpoints.length; k += 1) {
    const later = props.checkpoints[k]
    if (later.step !== current.step || later.mode !== 'click') continue
    const el = tourEl(later.target)
    const alt = later.alt ? tourEl(later.alt) : null
    if (el?.contains(node) || alt?.contains(node)) return k
  }
  return null
}

function valueOf(event: Event): string {
  const el = event.target as HTMLInputElement | null
  return (el?.value || '').trim()
}

function onClick(event: MouseEvent): void {
  // A header link is navigation away, and navigation ends the tutorial. Only
  // links: the header also carries the profile-drawer button, and opening the
  // drawer to read something is not leaving.
  const anchor = (event.target as HTMLElement | null)?.closest?.<HTMLAnchorElement>(
    '.top-nav a[href]'
  )
  if (anchor) {
    emit('exit', navigatesAwayFrom(anchor) ? 'nav' : 'link')
    return
  }

  // Acted ahead within this step: catch up past the checkpoint they just did.
  // Same synchronous rule as below for a click that ends the page.
  const ahead = within(event) ? null : laterClickCheckpointHit(event)
  if (ahead !== null) {
    window.clearTimeout(clickTimer)
    if (props.checkpoints[ahead].advanceImmediately) {
      emit('jump', ahead + 1)
      return
    }
    clickTimer = window.setTimeout(() => emit('jump', ahead + 1), CLICK_ADVANCE_DELAY_MS)
    return
  }

  if (cp.value?.mode !== 'click' || !within(event)) return
  window.clearTimeout(clickTimer)

  // A checkpoint whose click leaves the page (ne-save) advances HERE, inside the
  // capture-phase listener, so the parent has written the new index to
  // sessionStorage before the app's own submit handler is even called. Deferring
  // it races the navigation, and the navigation wins on a quick server: the run
  // resumes on the checkpoint just finished. See TourCheckpoint.advanceImmediately.
  if (cp.value.advanceImmediately) {
    emit('advance')
    return
  }

  clickTimer = window.setTimeout(() => emit('advance'), CLICK_ADVANCE_DELAY_MS)
}

/**
 * Readiness after the seed above follows the live events. The handoff README
 * wanted a prefilled value to count for nothing, so the pricing step could not
 * be walked in silence; the seed only ever lights Next — nothing advances
 * without the florist pressing it or moving on — so that concern does not
 * apply, and Hannah ruled on 2026-09-08 that a filled field should show Next
 * lit.
 *
 * `change` counts as well as `input` because ne-client is the real form's
 * <select> over the account's clients, which never fires `input` when picked
 * from the list — and the inline "Add new client" flow dispatches `change`
 * explicitly (app/views/events/partials/_scripts.html.erb).
 */
function onInput(event: Event): void {
  if (cp.value?.mode !== 'input' || !within(event)) return
  ready.value = valueOf(event).length > 0
}

function onKeydown(event: KeyboardEvent): void {
  if (cp.value?.mode !== 'input' || event.key !== 'Enter' || !within(event)) return

  // preventDefault unconditionally, not only when the field has a value. These
  // fields sit inside #newEventForm, where Enter natively submits — which would
  // create the event several checkpoints early and skip the rest of the form.
  // The prototype only blocked it once the field was filled, leaving exactly
  // the empty-field case that submits.
  event.preventDefault()
  if (ready.value) emit('advance')
}

function onFocusout(event: FocusEvent): void {
  if (cp.value?.mode !== 'input' || !within(event) || !ready.value) return
  const next = event.relatedTarget as Node | null
  // Moving on to another field is "done with this one"; clicking Next on the
  // card is the card's own business and must not double-advance.
  if (!next || cardRef.value?.contains(next)) return
  emit('advance')
}

function onViewportChange(): void {
  measure()
}

// ── lifecycle ─────────────────────────────────────────────────────────────

function focusTarget(): void {
  if (cp.value?.mode !== 'input') return
  const el = tourEl(cp.value.target)
  if (el && document.activeElement !== el) el.focus({ preventScroll: true })
}

/**
 * An intro's button: close what the checkpoint says to (a picker left open)
 * before moving on, so the next card is not pointing at a screen behind a
 * modal. `.click()` on the real Done button, so the app's own close handler
 * runs — the tour never reaches into the editor's state.
 */
function onIntroAdvance(): void {
  const dismiss = cp.value?.dismiss
  if (dismiss) tourEl(dismiss)?.click()
  emit('advance')
}

function enterCheckpoint(): void {
  // A `look` checkpoint asks nothing of the florist: Next is lit from the start.
  ready.value = cp.value?.mode === 'look'
  rect.value = null
  onOtherTab.value = false
  revealedForCheckpoint = false
  scrolledForCheckpoint = false
  focusedForCheckpoint = false
  seededForCheckpoint = false
  window.clearTimeout(clickTimer)
  measure()
  startMissingTargetTimer()
  // Measure again after the card has rendered, so its real height is in hand
  // before the placement decides whether it fits below the target.
  nextTick(measure)
}

onMounted(() => {
  document.addEventListener('click', onClick, true)
  document.addEventListener('input', onInput, true)
  document.addEventListener('change', onInput, true)
  document.addEventListener('keydown', onKeydown, true)
  document.addEventListener('focusout', onFocusout, true)
  window.addEventListener('resize', onViewportChange)
  window.addEventListener('scroll', onViewportChange, true)

  enterCheckpoint()
  frame = window.requestAnimationFrame(loop)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onClick, true)
  document.removeEventListener('input', onInput, true)
  document.removeEventListener('change', onInput, true)
  document.removeEventListener('keydown', onKeydown, true)
  document.removeEventListener('focusout', onFocusout, true)
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)

  if (frame) window.cancelAnimationFrame(frame)
  window.clearTimeout(clickTimer)
  clearMissingTargetTimer()
})

watch(() => props.index, enterCheckpoint)
</script>

<template>
  <div v-if="cp" class="tour-run">
    <div
      v-for="(s, k) in scrims"
      :key="k"
      class="tour-scrim"
      :style="{ top: `${s.top}px`, left: `${s.left}px`, width: `${s.width}px`, height: `${s.height}px` }"
    />

    <div
      v-if="hole"
      class="tour-spot"
      :style="{ top: `${hole.top}px`, left: `${hole.left}px`, width: `${hole.width}px`, height: `${hole.height}px` }"
    />

    <div
      ref="cardRef"
      class="tour-card"
      :class="{
        'tour-card--intro': intro,
        'tour-card--docked': docked,
        'tour-card--above': placement?.above,
      }"
      :style="cardStyle"
      role="dialog"
      aria-live="polite"
    >
      <span
        v-if="placement"
        class="tour-card__tail"
        :style="{ left: `${placement.tailLeft}px` }"
      />

      <div class="tour-card__head">
        <span class="bz-eyebrow">{{ stepLabel }}</span>
        <button type="button" class="tour-card__exit" @click="emit('exit', 'link')">
          Exit tutorial
        </button>
      </div>

      <div class="tour-dots">
        <i
          v-for="(_, k) in steps"
          :key="k"
          :class="{ done: k < (cp.step ?? 0), on: k === cp.step }"
        />
      </div>

      <h3 class="tour-card__title">{{ cp.title }}</h3>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p class="tour-card__body" v-html="bodyHtml" />
      <p v-if="onOtherTab" class="tour-card__hint">
        That's on the <b>{{ otherTabLabel }}</b> tab — click it to carry on.
      </p>

      <div class="tour-card__foot">
        <span class="tour-card__nav">
          <button v-if="canGoBack" type="button" class="tour-link tour-card__back" @click="emit('back')">
            Back
          </button>
          <button v-if="intro" type="button" class="tour-link tour-card__skip" @click="emit('skip-step')">
            {{ skipLabel }}
          </button>
          <span v-else-if="cp.mode !== 'look'" class="tour-wait" :class="{ 'tour-wait--ready': ready }">
            {{ cp.mode === 'input' && ready ? 'Looks good' : 'Waiting for you…' }}
          </span>
        </span>
        <template v-if="intro">
          <button type="button" class="bz-btn bz-btn-primary bz-btn-sm" @click="onIntroAdvance">
            {{ cp.cta ?? 'Show me' }}
          </button>
        </template>
        <template v-else>
          <button
            v-if="cp.mode === 'input' || cp.mode === 'look'"
            type="button"
            class="bz-btn bz-btn-primary bz-btn-sm"
            :disabled="!ready"
            @click="emit('advance')"
          >
            Next
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
